import { useEffect, useMemo, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import axiosInstance from "api";
import moment from "moment";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

type ChatMsg = { role: "assistant" | "user"; text: string };

declare global {
  interface Window {
    webkitSpeechRecognition?: any;
    SpeechRecognition?: any;
  }
}

function getSpeechRecognition(): any | null {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

const WAKE_PHRASES = [
  "hey servix",
  "hi servix",
  "hello servix",
  "ok servix",
  "okay servix",
  // common mishears
  "hey service",
  "hi service",
  "hey cervix",
  "hi cervix",
  // bare wake
  "servix",
] as const;

function normalizeSpokenText(raw: string) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isIncompleteVoiceUtterance(raw: string) {
  const t = normalizeSpokenText(raw);
  if (!t) return true;
  // Example failures from voice: "and password is" / "email is"
  // These should NOT auto-send; wait for the actual value.
  if (/^(and\s+)?(name|email|phone|mobile|number|password|pass)\s*(is)?$/.test(t))
    return true;
  if (/^(and\s+)?phone\s+number\s*(is)?$/.test(t)) return true;
  if (/\b(password|pass|email|phone|mobile|number|name)\s+is\s*$/.test(t))
    return true;
  return false;
}

export default function HeyTix() {
  const [messages, setMessages] = useState<ChatMsg[]>(() => [
    {
      role: "assistant",
      text:
        'Hi! I’m Hey Servix. How can I help you today?\n\n- Create customer\n- Create asset\n- Create ticket\n\nTip: Click the mic and say "Hey Servix" to start.',
    },
  ]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any | null>(null);
  const micWantedRef = useRef(false);
  const autoSendTimerRef = useRef<number | null>(null);
  const lastSpeechTextRef = useRef<string>("");
  const scrollEndRef = useRef<HTMLDivElement | null>(null);

  type Flow =
    | { kind: "idle" }
    | {
        kind: "createCustomer";
        step: "cust_name" | "cust_email" | "cust_phone" | "cust_password";
        data: Record<string, any>;
      }
    | {
        kind: "createMember";
        step:
          | "mem_name"
          | "mem_email"
          | "mem_phone"
          | "mem_password"
          | "mem_skills"
          | "confirm";
        data: Record<string, any>;
      }
    | {
        kind: "createAssetType";
        step: "name" | "confirm";
        data: Record<string, any>;
      }
    | {
        kind: "createSubscriptionType";
        step:
          | "asset_type_pick"
          | "sub_name"
          | "sub_description"
          | "sub_price"
          | "sub_duration_days"
          | "sub_free_service"
          | "sub_free_count"
          | "confirm";
        data: Record<string, any>;
      }
    | {
        kind: "createAsset";
        step:
          | "customer_lookup"
          | "customer_pick"
          | "asset_type_pick"
          | "asset_name"
          | "asset_sku"
          | "asset_purchased_date"
          | "address_pick"
          | "confirm";
        data: Record<string, any>;
      }
    | {
        kind: "createTicket";
        step:
          | "customer_lookup"
          | "customer_pick"
          | "asset_pick"
          | "category_pick"
          | "ticket_title"
          | "ticket_description"
          | "confirm";
        data: Record<string, any>;
      };

  const [flow, setFlow] = useState<Flow>({ kind: "idle" });
  const flowRef = useRef<Flow>({ kind: "idle" });

  const setFlowSync = (next: Flow) => {
    flowRef.current = next;
    setFlow(next);
  };

  useEffect(() => {
    flowRef.current = flow;
  }, [flow]);

  const speechSupported = useMemo(() => !!getSpeechRecognition(), []);

  useEffect(() => {
    return () => {
      try {
        micWantedRef.current = false;
        if (autoSendTimerRef.current) {
          window.clearTimeout(autoSendTimerRef.current);
          autoSendTimerRef.current = null;
        }
        recognitionRef.current?.stop?.();
      } catch (_) {}
      recognitionRef.current = null;
    };
  }, []);

  const append = (msg: ChatMsg) => setMessages((m) => [...m, msg]);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const extractEmail = (raw: string) => {
    const m = raw.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
    return m?.[0]?.trim() || "";
  };

  const extractPhone = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, "");
    if (digits.length < 10) return "";
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const canAutoSendVoiceForCurrentStep = (raw: string) => {
    const text = String(raw || "").trim();
    if (!text) return false;
    if (isIncompleteVoiceUtterance(text)) return false;

    const f = flowRef.current;
    // If we're not in a guided flow, auto-send is fine.
    if (
      f.kind !== "createCustomer" &&
      f.kind !== "createMember" &&
      f.kind !== "createAssetType" &&
      f.kind !== "createSubscriptionType"
    ) {
      return true;
    }

    // In create-customer flow, only auto-send when the expected field is present.
    if (f.step === "cust_name") {
      const name = extractName(text) || text.trim();
      return !!name && name.length >= 2;
    }
    if (f.step === "cust_email") {
      return !!extractEmail(text);
    }
    if (f.step === "cust_phone") {
      return !!extractPhoneField(text);
    }
    if (f.step === "cust_password") {
      // When in confirm mode, allow "yes/confirm/no/cancel" as a final voice auto-send.
      if ((f.data as any)?.__confirm === true) {
        const t = normalizeSpokenText(text);
        return (
          t.includes("yes") ||
          t.includes("confirm") ||
          t.includes("no") ||
          t.includes("cancel")
        );
      }
      const pw = extractPassword(text);
      return validateCustomerField("password", pw) === "";
    }
    if (f.kind === "createMember") {
      if (f.step === "mem_name") return (extractName(text) || text).trim().length >= 2;
      if (f.step === "mem_email") return !!extractEmail(text);
      if (f.step === "mem_phone") return !!extractPhoneField(text);
      if (f.step === "mem_password") {
        if ((f.data as any)?.__confirm === true) {
          const t = normalizeSpokenText(text);
          return (
            t.includes("yes") ||
            t.includes("confirm") ||
            t.includes("no") ||
            t.includes("cancel")
          );
        }
        const pw = extractPassword(text);
        return validateCustomerField("password", pw) === "";
      }
      if (f.step === "mem_skills") return String(text).trim().length >= 2;
      if (f.step === "confirm") {
        const t = normalizeSpokenText(text);
        return (
          t.includes("yes") ||
          t.includes("confirm") ||
          t.includes("no") ||
          t.includes("cancel")
        );
      }
    }
    if (f.kind === "createAssetType") {
      if (f.step === "name") return text.trim().length >= 2;
      if (f.step === "confirm") {
        const t = normalizeSpokenText(text);
        return t.includes("yes") || t.includes("confirm") || t.includes("no") || t.includes("cancel");
      }
    }
    if (f.kind === "createSubscriptionType") {
      if (f.step === "asset_type_pick") return text.trim().length >= 1;
      if (f.step === "sub_name") return text.trim().length >= 2;
      if (f.step === "sub_description") return text.trim().length >= 2;
      if (f.step === "sub_price") return /\d/.test(text);
      if (f.step === "sub_duration_days") return /\d/.test(text);
      if (f.step === "sub_free_service") {
        const t = normalizeSpokenText(text);
        return t.includes("yes") || t.includes("no") || t.includes("confirm");
      }
      if (f.step === "sub_free_count") return /\d/.test(text);
      if (f.step === "confirm") {
        const t = normalizeSpokenText(text);
        return t.includes("yes") || t.includes("confirm") || t.includes("no") || t.includes("cancel");
      }
    }
    return true;
  };

  const extractLabeled = (raw: string, label: string) => {
    // Supports:
    // - "label: value", "label=value"
    // - "label is value"
    // Stops at: ",", newline, or before "and <next field>"
    const stopBefore =
      "(?=\\s*(?:,|\\n|\\band\\b\\s+(?:name|email|phone|phone\\s+number|mobile|number|password|pass)\\b|$))";
    const re = new RegExp(
      `\\b${label}\\b\\s*(?::|=|\\bis\\b)\\s*([^\\n,]+?)\\s*${stopBefore}`,
      "i",
    );
    const m = raw.match(re);
    return m?.[1]?.trim() || "";
  };

  const extractName = (raw: string) => {
    const labeled = extractLabeled(raw, "name");
    if (labeled) return labeled.trim();
    // Natural forms:
    // - "customer name is X"
    // - "with the name X"
    // - "name X" (without "is")
    const m =
      raw.match(
        /\bcustomer\s+name\b\s*(?::|=|\bis\b)\s*([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:email|phone|phone\s+number|mobile|number|password|pass)\b|$))/i,
      ) ||
      raw.match(
        /\bname\b\s+(?:is\s+)?([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:email|phone|phone\s+number|mobile|number|password|pass)\b|$))/i,
      );
    return m?.[1]?.trim() || "";
  };

  const extractPhoneField = (raw: string) => {
    // "phone number is ..." is common; accept it explicitly.
    const labeled =
      extractLabeled(raw, "phone number") ||
      extractLabeled(raw, "phone") ||
      extractLabeled(raw, "mobile") ||
      extractLabeled(raw, "number");
    if (labeled) return extractPhone(labeled);
    return extractPhone(raw);
  };

  const extractPassword = (raw: string) => {
    const labeled = extractLabeled(raw, "password") || extractLabeled(raw, "pass");
    if (labeled) return labeled.trim();
    // Common natural language forms: "password is X", "pass is X"
    const m = raw.match(/\b(pass(word)?)\b\s+is\s+([^\s,]+)\s*$/i);
    if (m?.[3]) return m[3].trim();
    // If user replies with only a single token, treat it as the password candidate.
    // (This fixes the UX where the bot asks "re-enter password" and user types just the password.)
    const oneToken = raw.trim();
    if (!oneToken) return "";
    if (/\s/.test(oneToken)) return "";
    return oneToken;
  };

  const extractSkills = (raw: string) => {
    const labeled = extractLabeled(raw, "skills") || extractLabeled(raw, "skill");
    if (labeled) return labeled.trim();
    const m = raw.match(/\bskills?\b\s+(?:are|is\s+)?([^\n,]+)\s*$/i);
    return m?.[1]?.trim() || "";
  };

  const extractAddressField = (raw: string) => {
    const labeled =
      extractLabeled(raw, "address") ||
      extractLabeled(raw, "location") ||
      extractLabeled(raw, "place");
    return labeled.trim();
  };

  const parseRegionId = (raw: string) => {
    const m = String(raw || "").match(/\b(\d+)\b/);
    return m ? Number(m[1]) : NaN;
  };

  const extractAssetTypeNameForCreate = (raw: string) => {
    const labeled = extractLabeled(raw, "asset type") || extractLabeled(raw, "type") || extractLabeled(raw, "name");
    if (labeled) return labeled.trim();
    const m = raw.match(/\basset\s+type\b\s+(?:name\s+)?(?:is\s+)?([^\n,]+)\s*$/i);
    return m?.[1]?.trim() || "";
  };

  const extractPrice = (raw: string) => {
    const labeled = extractLabeled(raw, "price") || extractLabeled(raw, "amount");
    const t = labeled || raw;
    const m = String(t || "").match(/(\d+(?:\.\d{1,2})?)/);
    return m ? Number(m[1]) : NaN;
  };

  const extractDurationDays = (raw: string) => {
    const labeled =
      extractLabeled(raw, "duration") ||
      extractLabeled(raw, "days") ||
      extractLabeled(raw, "duration in days");
    const t = labeled || raw;
    const m = String(t || "").match(/(\d{1,5})/);
    return m ? Number(m[1]) : NaN;
  };

  const parseYesNo = (raw: string) => {
    const t = normalizeSpokenText(raw);
    if (t.includes("no") || t.includes("cancel")) return false;
    if (t.includes("yes") || t.includes("confirm")) return true;
    return null;
  };

  const extractFreeCount = (raw: string) => {
    const labeled = extractLabeled(raw, "count") || extractLabeled(raw, "free service count");
    const t = labeled || raw;
    const m = String(t || "").match(/(\d{1,5})/);
    return m ? Number(m[1]) : NaN;
  };

  const parseCustomerDetails = (raw: string) => {
    const email = extractLabeled(raw, "email") || extractEmail(raw);
    const phone = extractPhoneField(raw);
    const password = extractPassword(raw);
    const explicitName = extractName(raw) || extractLabeled(raw, "customer");
    let name = explicitName;
    if (!name) {
      let cleaned = raw;
      if (email) cleaned = cleaned.replace(email, " ");
      if (phone) cleaned = cleaned.replace(phone, " ");
      if (password) cleaned = cleaned.replace(password, " ");
      cleaned = cleaned.replace(
        /\b(email|phone|phone\s+number|mobile|number|password|pass|name|customer\s+name)\b\s*(?::|=|\bis\b)?\s*/gi,
        " ",
      );
      cleaned = cleaned.replace(/[,|]/g, " ");
      cleaned = cleaned.replace(/\s+/g, " ").trim();
      name = cleaned;
    }
    return {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      password: password.trim(),
    };
  };

  const parseIdNumber = (raw: string) => {
    const m = raw.match(/\b\d+\b/);
    return m ? Number(m[0]) : NaN;
  };

  const parseNaturalDateToYmd = (raw: string) => {
    const t = String(raw || "").trim();
    if (!t) return "";
    // Prefer strict ISO first
    const iso = moment(t, "YYYY-MM-DD", true);
    if (iso.isValid()) return iso.format("YYYY-MM-DD");
    // Handle common human formats loosely
    const cleaned = t
      .replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, "$1")
      .replace(/[,]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const m = moment(cleaned, ["D MMM YYYY", "D MMMM YYYY", "MMM D YYYY", "MMMM D YYYY", "D/M/YYYY", "D-M-YYYY"], true);
    if (m.isValid()) return m.format("YYYY-MM-DD");
    const loose = moment(cleaned);
    return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
  };

  const extractAssetTypeName = (raw: string) => {
    const labeled =
      extractLabeled(raw, "asset type") ||
      extractLabeled(raw, "assettype") ||
      extractLabeled(raw, "type");
    if (labeled) return labeled.trim();
    const m = raw.match(
      /\basset\s+type\b\s+(?:is\s+)?([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:asset|brand|name|sku|serial|date|purchased|purchase|address|customer|email|phone)\b|$))/i,
    );
    return m?.[1]?.trim() || "";
  };

  const extractCustomerNameForAsset = (raw: string) => {
    // Accept: "for customer ram", "customer name ram", "customer with name ram", "for ram"
    const labeled =
      extractLabeled(raw, "customer name") ||
      extractLabeled(raw, "customer") ||
      extractName(raw);
    if (labeled) return labeled.trim();
    const m =
      raw.match(
        /\bfor\s+customer\b\s+(?:named\s+|name\s+)?([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:asset|asset\s+type|type|brand|sku|date|purchased|purchase|address)\b|$))/i,
      ) ||
      // Also accept: "create asset for Sravani"
      raw.match(
        /\bfor\b\s+(?:the\s+)?(?:customer\s+)?([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:customer|asset|asset\s+type|type|brand|sku|date|purchased|purchase|address|email|phone)\b|$))/i,
      );
    return m?.[1]?.trim() || "";
  };

  const normalizeCustomerSearchQuery = (raw: string) => {
    return String(raw || "")
      .trim()
      .replace(/^(the\s+)?customer\s+/i, "")
      .replace(/^(for\s+)(the\s+)?/i, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const extractBrandName = (raw: string) => {
    const labeled =
      extractLabeled(raw, "brand") ||
      extractLabeled(raw, "brand name") ||
      extractLabeled(raw, "asset name") ||
      // Support: "asset is Onida"
      extractLabeled(raw, "asset") ||
      extractLabeled(raw, "name");
    return labeled.trim();
  };

  const extractSku = (raw: string) => {
    const labeled = extractLabeled(raw, "sku") || extractLabeled(raw, "serial");
    if (labeled) return labeled.trim();
    const m = raw.match(/\bsku\b\s+(?:is\s+)?([^\s,]+)\s*$/i);
    return m?.[1]?.trim() || "";
  };

  const looksLikeAskForList = (raw: string) => {
    const t = raw.toLowerCase();
    return (
      t.includes("what") ||
      t.includes("which") ||
      t.includes("list") ||
      t.includes("show") ||
      t.includes("available") ||
      t.includes("types have") ||
      t.includes("asset types")
    );
  };

  const parsePickIndex = (raw: string) => {
    const m = String(raw || "").trim().match(/^#?\s*(\d+)\b/);
    if (!m) return NaN;
    return Number(m[1]);
  };

  const extractCategoryName = (raw: string) => {
    const labeled = extractLabeled(raw, "category");
    if (labeled) return labeled.trim();
    const m = raw.match(
      /\bcategory\b\s+(?:is\s+)?([^\n,]+?)(?=\s*(?:,|\n|\band\b\s+(?:title|description|asset|sku|serial|issue|problem|customer|email|phone)\b|$))/i,
    );
    return m?.[1]?.trim() || "";
  };

  const extractTicketTitle = (raw: string) => {
    const labeled = extractLabeled(raw, "title") || extractLabeled(raw, "subject");
    return labeled.trim();
  };

  const extractTicketDescription = (raw: string) => {
    const labeled = extractLabeled(raw, "description") || extractLabeled(raw, "desc") || extractLabeled(raw, "issue") || extractLabeled(raw, "problem");
    return labeled.trim();
  };

  const extractAssetQueryForTicket = (raw: string) => {
    const sku = extractSku(raw);
    const labeledAsset =
      extractLabeled(raw, "asset") ||
      extractLabeled(raw, "asset name") ||
      extractLabeled(raw, "product") ||
      "";
    const assetName = labeledAsset.trim();
    // If user says "asset is X" we already support extractLabeled("asset") via extractBrandName,
    // but for tickets we treat it as the asset name selector.
    const oneToken = String(raw || "").trim();
    const fallbackName =
      !assetName && !sku && oneToken && !/\s/.test(oneToken) ? oneToken : "";
    return { assetName: assetName || fallbackName, sku };
  };

  const looksLikeAskForCategories = (raw: string) => {
    const t = raw.toLowerCase();
    return t.includes("category") && (t.includes("list") || t.includes("show") || t.includes("available") || t.includes("what") || t.includes("which"));
  };

  const validateCustomerField = (
    field: "name" | "email" | "phone" | "password",
    value: string,
  ): string => {
    const v = (value ?? "").trim();
    if (!v) return `${field} is required`;
    if (field === "email") {
      const ok = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(v);
      return ok ? "" : "Invalid email format";
    }
    if (field === "phone") {
      const ok = /^\d{10}$/.test(v);
      return ok ? "" : "Enter a valid phone number (10 digits)";
    }
    if (field === "password") {
      if (v.length < 6) return "Password is too short (min 6 characters)";
      const ok =
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/.test(v);
      return ok
        ? ""
        : "Password must contain at least one letter, one number, and one special character";
    }
    return "";
  };

  const missingCustomerFields = (data: Record<string, any>) => {
    const missing: Array<"name" | "email" | "phone" | "password"> = [];
    if (!(data.customer_name || "").trim()) missing.push("name");
    if (!(data.customer_email || "").trim()) missing.push("email");
    if (!(data.customer_phone || "").trim()) missing.push("phone");
    if (!(data.customer_password || "").trim()) missing.push("password");
    return missing;
  };

  const mergeCustomerDetails = (draft: Record<string, any>, raw: string) => {
    const parsed = parseCustomerDetails(raw);
    const next = { ...draft };
    if (!next.customer_name && parsed.name) next.customer_name = parsed.name;
    if (!next.customer_email && parsed.email) next.customer_email = parsed.email;
    if (!next.customer_phone && parsed.phone) next.customer_phone = parsed.phone;
    if (!next.customer_password && parsed.password) next.customer_password = parsed.password;
    return next;
  };

  const detectIntentText = (raw: string) => {
    const t = raw.toLowerCase().trim();
    // tolerate typos like "createb customer", "create customer"
    if (/\bcreate\w*\s+customer\b/.test(t) || /\badd\w*\s+customer\b/.test(t)) {
      return "create_customer";
    }
    if (
      /\bcreate\w*\s+ticket\b/.test(t) ||
      /\braise\w*\s+ticket\b/.test(t) ||
      /\badd\w*\s+ticket\b/.test(t)
    ) {
      return "create_ticket";
    }
    if (/\bcreate\w*\s+asset\s+type\b/.test(t) || /\badd\w*\s+asset\s+type\b/.test(t)) {
      return "create_asset_type";
    }
    if (
      /\bcreate\w*\s+subscription\s*type\b/.test(t) ||
      /\badd\w*\s+subscription\s*type\b/.test(t) ||
      /\bcreate\w*\s+subscription\b/.test(t) ||
      /\badd\w*\s+subscription\b/.test(t)
    ) {
      return "create_subscription_type";
    }
    // IMPORTANT: keep this AFTER "asset type" intent so "create asset type"
    // doesn't get misclassified as "create asset".
    if (/\bcreate\w*\s+asset\b/.test(t) || /\badd\w*\s+asset\b/.test(t)) {
      return "create_asset";
    }
    if (
      /\bcreate\w*\s+(an?\s+)?employee\b/.test(t) ||
      /\badd\w*\s+(an?\s+)?employee\b/.test(t)
    ) {
      return "create_employee";
    }
    if (
      /\bcreate\w*\s+(a\s+)?stock\s*manager\b/.test(t) ||
      /\badd\w*\s+(a\s+)?stock\s*manager\b/.test(t) ||
      /\bcreate\w*\s+stockmanager\b/.test(t) ||
      /\badd\w*\s+stockmanager\b/.test(t)
    ) {
      return "create_stock_manager";
    }
    if (t === "hey tix" || t === "hi" || t === "hello" || t === "hey") {
      return "greet";
    }
    return "unknown";
  };

  const handleSend = (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text) return;
    append({ role: "user", text });
    if (!overrideText) setInput("");

    const intent = detectIntentText(text);
    if (intent === "greet") {
      append({
        role: "assistant",
        text:
          "Hi! Tell me what you want to do.\n\nExamples:\n- Create customer\n- Create asset\n- Create ticket",
      });
      return;
    }

    if (intent === "create_customer") {
      // Parse this SAME message for any provided fields.
      const withoutIntent = text
        .replace(/\b(create\w*|add\w*)\s+customer\b/i, "")
        .trim();
      const seeded = mergeCustomerDetails(
        {
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          customer_password: "",
        },
        withoutIntent,
      );
      const missing = missingCustomerFields(seeded);
      setFlowSync({ kind: "createCustomer", step: "cust_name", data: seeded });
      if (missing.length === 0) {
        append({
          role: "assistant",
          text:
            `Please confirm:\n` +
            `Name: ${seeded.customer_name}\n` +
            `Email: ${seeded.customer_email}\n` +
            `Phone: ${seeded.customer_phone}\n\n` +
            `Create this customer? (yes/no)`,
        });
        setFlowSync({
          kind: "createCustomer",
          step: "cust_password",
          data: { ...seeded, __confirm: true },
        });
      } else {
        const got = [
          seeded.customer_name ? "name" : "",
          seeded.customer_email ? "email" : "",
          seeded.customer_phone ? "phone" : "",
          seeded.customer_password ? "password" : "",
        ]
          .filter(Boolean)
          .join(", ");
        append({
          role: "assistant",
          text:
            `${got ? `Got your ${got}.\n\n` : ""}` +
            `Please share: ${missing.join(", ")}.`,
        });
      }
      return;
    }

    if (intent === "create_asset_type") {
      const withoutIntent = text
        .replace(/\b(create\w*|add\w*)\s+asset\s+type\b/i, "")
        .trim();
      const name = extractAssetTypeNameForCreate(withoutIntent) || withoutIntent.trim();
      const seeded: Record<string, any> = { asset_type_name: name, __confirm: false };
      setFlowSync({ kind: "createAssetType", step: "name", data: seeded });
      if (!name) {
        append({ role: "assistant", text: "Asset type name?" });
        return;
      }
      const draft =
        `Please confirm:\n` +
        `Asset Type Name: ${name}\n\n` +
        `Create this asset type? (yes/no)`;
      setFlowSync({ kind: "createAssetType", step: "confirm", data: { ...seeded, __confirm: true } });
      append({ role: "assistant", text: draft });
      return;
    }

    if (intent === "create_subscription_type") {
      const withoutIntent = text
        .replace(/\b(create\w*|add\w*)\s+subscription\s*type\b/i, "")
        .replace(/\b(create\w*|add\w*)\s+subscription\b/i, "")
        .trim();

      const seeded: Record<string, any> = {
        asset_type_name: extractAssetTypeName(withoutIntent),
        asset_type_id: null,
        subscription_type_name: extractLabeled(withoutIntent, "subscription type") || extractLabeled(withoutIntent, "name"),
        description: extractLabeled(withoutIntent, "description") || extractLabeled(withoutIntent, "desc"),
        price: extractPrice(withoutIntent),
        duration_in_days: extractDurationDays(withoutIntent),
        include_free_service: null,
        free_service_count: extractFreeCount(withoutIntent),
        __assetTypes: null,
        __confirm: false,
      };

      setFlowSync({ kind: "createSubscriptionType", step: "asset_type_pick", data: seeded });
      append({
        role: "assistant",
        text: seeded.asset_type_name
          ? `Okay — creating a subscription type for asset type "${seeded.asset_type_name}".`
          : "Okay — let's create a subscription type.\n\nAsset type?",
      });
      return;
    }

    if (intent === "create_employee" || intent === "create_stock_manager") {
      const roleId = intent === "create_employee" ? 4 : 6;
      const roleLabel = intent === "create_employee" ? "Employee" : "Stock Manager";
      const withoutIntent = text
        .replace(/\b(create\w*|add\w*)\s+(an?\s+)?employee\b/i, "")
        .replace(/\b(create\w*|add\w*)\s+(a\s+)?stock\s*manager\b/i, "")
        .replace(/\b(create\w*|add\w*)\s+stockmanager\b/i, "")
        .trim();

      const seeded: Record<string, any> = {
        role_id: roleId,
        role_label: roleLabel,
        name: extractName(withoutIntent),
        email: extractLabeled(withoutIntent, "email") || extractEmail(withoutIntent),
        phone: extractPhoneField(withoutIntent),
        password: extractPassword(withoutIntent),
        skill_set: extractSkills(withoutIntent),
        // Auto-filled (we don't ask these in chat)
        region_id: parseRegionId(withoutIntent),
        address: extractAddressField(withoutIntent),
        __regions: null,
        __confirm: false,
      };

      const missing: string[] = [];
      if (!String(seeded.name || "").trim()) missing.push("name");
      if (!String(seeded.email || "").trim()) missing.push("email");
      if (!String(seeded.phone || "").trim()) missing.push("phone");
      if (!String(seeded.password || "").trim()) missing.push("password");
      if (!String(seeded.skill_set || "").trim()) missing.push("skills");

      setFlowSync({ kind: "createMember", step: "mem_name", data: seeded });
      append({
        role: "assistant",
        text: `Okay — creating a **${roleLabel}**.\n\nPlease share: ${missing.join(", ")}.`,
      });
      return;
    }

    if (intent === "create_asset") {
      const withoutIntent = text.replace(/\b(create\w*|add\w*)\s+asset\b/i, "").trim();
      const seededCustomerName = extractCustomerNameForAsset(withoutIntent);
      const seededAssetTypeName = extractAssetTypeName(withoutIntent);
      const seededBrand = extractBrandName(withoutIntent);
      const seededSku = extractSku(withoutIntent);
      const seededDate = parseNaturalDateToYmd(withoutIntent);

      const seeded: Record<string, any> = {
        customer_name: seededCustomerName,
        customer_id: null,
        customer_candidates: null,
        asset_type_name: seededAssetTypeName,
        asset_type_id: null,
        asset_type_candidates: null,
        asset_name: seededBrand,
        asset_sku: seededSku,
        asset_purchased_date: seededDate,
        address_id: null,
        address_candidates: null,
        __confirm: false,
      };

      setFlowSync({ kind: "createAsset", step: "customer_lookup", data: seeded });
      append({
        role: "assistant",
        text: seededCustomerName
          ? `Okay — creating an asset for customer "${seededCustomerName}". Let me find them…`
          : "Okay — let's create an asset.\n\nCustomer name?",
      });
      if (seededCustomerName) return void handleCreateAssetFlow(seededCustomerName);
      return;
    }

    if (intent === "create_ticket") {
      const withoutIntent = text
        .replace(/\b(create\w*|raise\w*|add\w*)\s+ticket\b/i, "")
        .trim();
      const customerName = extractCustomerNameForAsset(withoutIntent);
      const { assetName, sku } = extractAssetQueryForTicket(withoutIntent);
      const categoryName = extractCategoryName(withoutIntent);
      const title = extractTicketTitle(withoutIntent);
      const description = extractTicketDescription(withoutIntent);

      const seeded: Record<string, any> = {
        customer_name: customerName,
        customer_id: null,
        customer_email: null,
        customer_candidates: null,
        asset_query_name: assetName,
        asset_query_sku: sku,
        asset_id: null,
        asset_type_id: null,
        address_id: null,
        asset_candidates: null,
        category_name: categoryName,
        category_id: null,
        category_candidates: null,
        title,
        description,
      };

      setFlowSync({ kind: "createTicket", step: "customer_lookup", data: seeded });
      append({
        role: "assistant",
        text: customerName
          ? `Okay — creating a ticket for customer "${customerName}". Let me find them…`
          : "Okay — let's create a ticket.\n\nCustomer name?",
      });
      if (customerName) return void handleCreateTicketFlow(customerName);
      return;
    }

    const currentFlow = flowRef.current;
    if (currentFlow.kind === "createCustomer") return void handleCreateCustomerFlow(text);
    if (currentFlow.kind === "createMember") return void handleCreateMemberFlow(text);
    if (currentFlow.kind === "createAssetType") return void handleCreateAssetTypeFlow(text);
    if (currentFlow.kind === "createSubscriptionType")
      return void handleCreateSubscriptionTypeFlow(text);
    if (currentFlow.kind === "createAsset") return void handleCreateAssetFlow(text);
    if (currentFlow.kind === "createTicket") return void handleCreateTicketFlow(text);

    append({
      role: "assistant",
      text:
        'I can help with: **create customer**, **create asset**, **create ticket**.\n\nType one of these to begin.',
    });
  };

  const handleSendClick = () => handleSend();

  const handleCreateCustomerFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createCustomer") return;
    let next = mergeCustomerDetails({ ...current.data }, answer);
    const step = current.step;

    type CreateCustomerStep = Extract<Flow, { kind: "createCustomer" }>["step"];
    const ask = (nextStep: CreateCustomerStep, prompt: string) => {
      setFlowSync({ kind: "createCustomer", step: nextStep, data: next });
      append({ role: "assistant", text: prompt });
    };

    try {
      const confirmMode = next.__confirm === true;
      if (confirmMode) {
        const t = answer.toLowerCase();
        if (t.includes("no") || t.includes("cancel")) {
          append({ role: "assistant", text: "Cancelled. Let me know if you want to start over." });
          setFlowSync({ kind: "idle" });
          return;
        }
        if (!(t.includes("yes") || t.includes("confirm"))) {
          append({ role: "assistant", text: 'Please reply "yes" to confirm or "no" to cancel.' });
          return;
        }
        append({ role: "assistant", text: "Creating customer…" });
        await axiosInstance.post("/customer", {
          customerData: {
            name: next.customer_name,
            email: next.customer_email,
            phone: next.customer_phone,
            password: next.customer_password,
            type: "customer",
          },
        });
        append({
          role: "assistant",
          text: `Customer ${next.customer_name} created successfully!`,
        });
        setFlowSync({ kind: "idle" });
        return;
      }

      // Validate provided fields; if invalid, ask only that field again.
      const validations: Array<["name" | "email" | "phone" | "password", string]> = [
        ["name", validateCustomerField("name", next.customer_name || "")],
        ["email", validateCustomerField("email", next.customer_email || "")],
        ["phone", validateCustomerField("phone", next.customer_phone || "")],
        ["password", validateCustomerField("password", next.customer_password || "")],
      ];
      const firstInvalid = validations.find(([, msg]) => !!msg);
      if (firstInvalid) {
        const [field, msg] = firstInvalid;
        const key =
          field === "name"
            ? "customer_name"
            : field === "email"
              ? "customer_email"
              : field === "phone"
                ? "customer_phone"
                : "customer_password";
        next = { ...next, [key]: "" };
        setFlowSync({ kind: "createCustomer", step: step, data: next });
        append({ role: "assistant", text: `${msg}\n\nPlease re-enter ${field}.` });
        return;
      }

      const missing = missingCustomerFields(next);
      if (missing.length) {
        setFlowSync({ kind: "createCustomer", step: step, data: next });
        append({
          role: "assistant",
          text: `Please share: ${missing.join(", ")}.`,
        });
        return;
      }

      // Confirmation before creating.
      append({
        role: "assistant",
        text:
          `Please confirm:\n` +
          `Name: ${next.customer_name}\n` +
          `Email: ${next.customer_email}\n` +
          `Phone: ${next.customer_phone}\n\n` +
          `Create this customer? (yes/no)`,
      });
      setFlowSync({
        kind: "createCustomer",
        step: "cust_password",
        data: { ...next, __confirm: true },
      });
      return;
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const handleCreateMemberFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createMember") return;
    let next = { ...current.data };
    const step = current.step;

    type MemberStep = Extract<Flow, { kind: "createMember" }>["step"];
    const ask = (nextStep: MemberStep, prompt: string) => {
      setFlowSync({ kind: "createMember", step: nextStep, data: next });
      append({ role: "assistant", text: prompt });
    };

    const ensureRegionsLoaded = async () => {
      if (Array.isArray(next.__regions)) return next.__regions as any[];
      const resp = await axiosInstance.get("/regions", { timeout: 15000 });
      const data = resp.data;
      const list = Array.isArray(data?.list) ? data.list : Array.isArray(data) ? data : [];
      next.__regions = list;
      return list;
    };

    try {
      const confirmMode = next.__confirm === true || step === "confirm";
      if (confirmMode) {
        const t = answer.toLowerCase();
        if (t.includes("no") || t.includes("cancel")) {
          append({
            role: "assistant",
            text: "Cancelled. Let me know if you want to start over.",
          });
          setFlowSync({ kind: "idle" });
          return;
        }
        if (!(t.includes("yes") || t.includes("confirm"))) {
          append({
            role: "assistant",
            text: 'Please reply "yes" to confirm or "no" to cancel.',
          });
          return;
        }

        append({
          role: "assistant",
          text: `Creating ${next.role_label || "member"}…`,
        });
        await axiosInstance.post("/employee/add-employee", {
          name: next.name,
          email: next.email,
          phone: next.phone,
          password: next.password,
          region_id: Number(next.region_id),
          address: next.address,
          skill_set: next.skill_set,
          role_id: [Number(next.role_id)],
        });
        append({
          role: "assistant",
          text: `${next.role_label || "Member"} created successfully!`,
        });
        setFlowSync({ kind: "idle" });
        return;
      }

      // merge likely fields from this answer (only fill missing ones)
      const name = extractName(answer);
      const email = extractLabeled(answer, "email") || extractEmail(answer);
      const phone = extractPhoneField(answer);
      const password = extractPassword(answer);
      const skills = extractSkills(answer);
      const address = extractAddressField(answer);
      const regionId = parseRegionId(answer);

      if (!String(next.name || "").trim() && name) next.name = name;
      if (!String(next.email || "").trim() && email) next.email = email;
      if (!String(next.phone || "").trim() && phone) next.phone = phone;
      if (!String(next.password || "").trim() && password) next.password = password;
      if (!String(next.skill_set || "").trim() && skills) next.skill_set = skills;
      // Skills should accept any free-form string (voice/typed), even without a label.
      if (step === "mem_skills" && !String(next.skill_set || "").trim()) {
        next.skill_set = String(answer || "").trim();
      }
      // We do NOT ask for region/address in chat. Auto-fill to satisfy backend validation.
      if (!Number.isFinite(Number(next.region_id)) || Number(next.region_id) <= 0) {
        const regions = await ensureRegionsLoaded();
        const first = Array.isArray(regions) && regions.length ? regions[0] : null;
        next.region_id = Number(first?.region_id) || 1;
      }
      if (!String(next.address || "").trim()) {
        next.address = "N/A";
      }

      if (!String(next.name || "").trim()) return ask("mem_name", "Member name?");
      if (!String(next.email || "").trim()) return ask("mem_email", "Email?");
      if (!String(next.phone || "").trim()) return ask("mem_phone", "Phone number?");
      if (!String(next.password || "").trim()) return ask("mem_password", "Password?");
      if (!String(next.skill_set || "").trim())
        return ask("mem_skills", "Skills? (Example: lift, escalator)");

      // Confirmation draft
      const draft =
        `Please confirm:\n` +
        `Role: ${next.role_label}\n` +
        `Name: ${next.name}\n` +
        `Email: ${next.email}\n` +
        `Phone: ${next.phone}\n` +
        `Skills: ${next.skill_set}\n\n` +
        `Create this ${String(next.role_label || "member").toLowerCase()}? (yes/no)`;

      setFlowSync({
        kind: "createMember",
        step: "confirm",
        data: { ...next, __confirm: true },
      });
      append({ role: "assistant", text: draft });
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const handleCreateAssetTypeFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createAssetType") return;
    let next = { ...current.data };
    const step = current.step;
    try {
      if (step === "name") {
        const name = extractAssetTypeNameForCreate(answer) || answer.trim();
        if (!name) {
          append({ role: "assistant", text: "Asset type name?" });
          return;
        }
        next.asset_type_name = name;
        const draft =
          `Please confirm:\n` +
          `Asset Type Name: ${name}\n\n` +
          `Create this asset type? (yes/no)`;
        setFlowSync({
          kind: "createAssetType",
          step: "confirm",
          data: { ...next, __confirm: true },
        });
        append({ role: "assistant", text: draft });
        return;
      }

      // confirm
      const t = answer.toLowerCase();
      if (t.includes("no") || t.includes("cancel")) {
        append({ role: "assistant", text: "Cancelled." });
        setFlowSync({ kind: "idle" });
        return;
      }
      if (!(t.includes("yes") || t.includes("confirm"))) {
        append({ role: "assistant", text: 'Please reply "yes" to confirm or "no" to cancel.' });
        return;
      }
      const name = String(next.asset_type_name || "").trim();
      if (!name) {
        setFlowSync({ kind: "createAssetType", step: "name", data: next });
        append({ role: "assistant", text: "Asset type name?" });
        return;
      }
      append({ role: "assistant", text: "Creating asset type…" });
      await axiosInstance.post("/asset-types", { asset_type_name: name });
      append({ role: "assistant", text: "Asset type created successfully." });
      setFlowSync({ kind: "idle" });
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const handleCreateSubscriptionTypeFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createSubscriptionType") return;
    let next = { ...current.data };
    const step = current.step;

    type SubStep = Extract<Flow, { kind: "createSubscriptionType" }>["step"];
    const ask = (nextStep: SubStep, prompt: string) => {
      setFlowSync({ kind: "createSubscriptionType", step: nextStep, data: next });
      append({ role: "assistant", text: prompt });
    };

    const ensureAssetTypesLoaded = async () => {
      if (Array.isArray(next.__assetTypes)) return next.__assetTypes as any[];
      const resp = await axiosInstance.get("/asset-types", { params: { is_active: 1 } });
      const list = Array.isArray(resp.data?.list) ? resp.data.list : [];
      next.__assetTypes = list;
      return list;
    };

    const renderAssetTypeChoices = (types: any[]) => {
      const lines = types.slice(0, 15).map((t, i) => `${i + 1}. ${t.asset_type_name}`);
      return `Which asset type?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(15, types.length)}) or type the name.`;
    };

    try {
      // Asset type pick
      if (step === "asset_type_pick") {
        const types = await ensureAssetTypesLoaded();
        const maybeIdx = parsePickIndex(answer);
        if (Number.isFinite(maybeIdx) && maybeIdx >= 1 && maybeIdx <= Math.min(15, types.length)) {
          const chosen = types[maybeIdx - 1];
          next.asset_type_id = chosen.asset_type_id;
          next.asset_type_name = chosen.asset_type_name;
          return ask("sub_name", "Subscription type name?");
        }
        const rawName = extractAssetTypeName(answer) || answer.trim();
        if (!rawName) {
          append({ role: "assistant", text: renderAssetTypeChoices(types) });
          return;
        }
        const normalized = rawName.toLowerCase();
        const match =
          types.find((t: any) => String(t?.asset_type_name || "").toLowerCase() === normalized) ||
          types.find((t: any) => String(t?.asset_type_name || "").toLowerCase().includes(normalized));
        if (!match) {
          append({ role: "assistant", text: `I couldn't match "${rawName}".` });
          append({ role: "assistant", text: renderAssetTypeChoices(types) });
          return;
        }
        next.asset_type_id = match.asset_type_id;
        next.asset_type_name = match.asset_type_name;
        return ask("sub_name", "Subscription type name?");
      }

      if (step === "sub_name") {
        const name =
          extractLabeled(answer, "subscription type") ||
          extractLabeled(answer, "name") ||
          answer.trim();
        if (!name) return ask("sub_name", "Subscription type name?");
        next.subscription_type_name = name;
        return ask("sub_description", "Description?");
      }

      if (step === "sub_description") {
        const desc = extractLabeled(answer, "description") || extractLabeled(answer, "desc") || answer.trim();
        if (!desc) return ask("sub_description", "Description?");
        next.description = desc;
        return ask("sub_price", "Price?");
      }

      if (step === "sub_price") {
        const price = extractPrice(answer);
        // DB column is DECIMAL(10,2) => max 99,999,999.99.
        // Also guard against users accidentally speaking a phone number.
        if (!Number.isFinite(price) || price < 0) {
          return ask("sub_price", "Price? (example: 999)");
        }
        if (price > 99999999.99) {
          return ask(
            "sub_price",
            "Price is too large. Please enter a valid amount (example: 999 or 9999.50).",
          );
        }
        const digitsOnly = String(answer || "").replace(/[^\d]/g, "");
        if (digitsOnly.length >= 10 && !String(answer || "").includes(".")) {
          return ask(
            "sub_price",
            "That looks like a phone number. Please enter the subscription price (example: 999).",
          );
        }
        next.price = price;
        return ask("sub_duration_days", "Duration in days?");
      }

      if (step === "sub_duration_days") {
        const days = extractDurationDays(answer);
        if (!Number.isFinite(days) || days <= 0) return ask("sub_duration_days", "Duration in days? (example: 365)");
        next.duration_in_days = days;
        return ask("sub_free_service", "Include free service? (yes/no)");
      }

      if (step === "sub_free_service") {
        const yn = parseYesNo(answer);
        if (yn == null) return ask("sub_free_service", 'Please reply "yes" or "no".');
        next.include_free_service = yn;
        if (!yn) {
          next.free_service_count = 0;
          return ask("confirm", "Okay. Please confirm to create. (yes/no)");
        }
        return ask("sub_free_count", "Free service count?");
      }

      if (step === "sub_free_count") {
        const c = extractFreeCount(answer);
        if (!Number.isFinite(c) || c <= 0) return ask("sub_free_count", "Free service count? (example: 2)");
        next.free_service_count = c;
        return ask("confirm", "Okay. Please confirm to create. (yes/no)");
      }

      // confirm
      const confirm = parseYesNo(answer);
      if (confirm == null) {
        append({ role: "assistant", text: 'Please reply "yes" to confirm or "no" to cancel.' });
        return;
      }
      if (!confirm) {
        append({ role: "assistant", text: "Cancelled." });
        setFlowSync({ kind: "idle" });
        return;
      }

      const payload = {
        subscription_type_name: String(next.subscription_type_name || "").trim(),
        asset_type_id: Number(next.asset_type_id),
        description: String(next.description || "").trim(),
        price: Number(next.price),
        duration_in_days: Number(next.duration_in_days),
        include_free_service: !!next.include_free_service,
        free_service_count: next.include_free_service ? Number(next.free_service_count || 0) : 0,
      };
      if (!payload.subscription_type_name || !payload.asset_type_id) {
        append({ role: "assistant", text: "Missing required fields. Please start again." });
        setFlowSync({ kind: "idle" });
        return;
      }
      append({ role: "assistant", text: "Creating subscription type…" });
      await axiosInstance.post("/subscription-types", payload);
      append({ role: "assistant", text: "Subscription type created successfully." });
      setFlowSync({ kind: "idle" });
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const handleCreateAssetFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createAsset") return;
    let next = { ...current.data };
    const step = current.step;

    type CreateAssetStep = Extract<Flow, { kind: "createAsset" }>["step"];
    const ask = (nextStep: CreateAssetStep, prompt: string) => {
      setFlowSync({ kind: "createAsset", step: nextStep, data: next });
      append({ role: "assistant", text: prompt });
    };

    const ensureCustomersLoaded = async () => {
      // Prefer on-demand server search to avoid loading huge customer lists.
      if (Array.isArray(next.__customers)) return next.__customers as any[];
      next.__customers = [];
      return next.__customers;
    };

    const searchCustomers = async (query: string) => {
      const q = String(query || "").trim();
      if (!q) return [];
      const resp = await axiosInstance.get("/customer/search", {
        params: { q, limit: 25 },
        timeout: 10000,
      });
      return Array.isArray(resp.data?.list) ? resp.data.list : [];
    };

    const ensureAssetTypesLoaded = async () => {
      if (Array.isArray(next.__assetTypes)) return next.__assetTypes as any[];
      const resp = await axiosInstance.get("/asset-types", { params: { is_active: 1 } });
      const list = Array.isArray(resp.data?.list) ? resp.data.list : [];
      next.__assetTypes = list;
      return list;
    };

    const ensureAddressesLoaded = async (customerId: number) => {
      if (Array.isArray(next.__addresses) && next.__addresses_customer_id === customerId) return next.__addresses as any[];
      const resp = await axiosInstance.get(`/address/customer/${customerId}`);
      const list = Array.isArray(resp.data?.list) ? resp.data.list : [];
      next.__addresses = list;
      next.__addresses_customer_id = customerId;
      return list;
    };

    const renderCustomerChoices = (candidates: any[]) => {
      const lines = candidates.slice(0, 10).map((c, i) => {
        const email = c?.email ? ` — ${c.email}` : "";
        const phone = c?.phone ? ` (${c.phone})` : "";
        return `${i + 1}. ${c.name}${email}${phone}`;
      });
      return `I found multiple customers with that name. Which one?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(
        10,
        candidates.length,
      )}).`;
    };

    const renderAssetTypeChoices = (types: any[]) => {
      const lines = types.slice(0, 15).map((t, i) => `${i + 1}. ${t.asset_type_name}`);
      return `Which asset type?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(15, types.length)}).`;
    };

    const renderAddressChoices = (addresses: any[]) => {
      const lines = addresses.slice(0, 10).map((a, i) => {
        const line = [a.address, a.address_type, a.region_name, a.city_name, a.state_name].filter(Boolean).join(", ");
        return `${i + 1}. ${line}`;
      });
      return `Which address should I use?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(10, addresses.length)}).`;
    };

    try {
      // Step: customer lookup (by name)
      if (step === "customer_lookup") {
        const extracted = extractCustomerNameForAsset(answer) || answer.trim();
        const rawName = normalizeCustomerSearchQuery(extracted);
        if (!rawName) return ask("customer_lookup", "Customer name?");
        next.customer_name = rawName;

        await ensureCustomersLoaded();
        let customers: any[] = [];
        try {
          customers = await searchCustomers(rawName);
        } catch (err: any) {
          const msg =
            err?.code === "ECONNABORTED"
              ? "Customer search timed out. Please try again."
              : "Customer search failed. Please try again.";
          return ask("customer_lookup", msg);
        }
        const matches = customers;

        if (matches.length === 0) {
          next.customer_name = "";
          return ask("customer_lookup", `No customer found for "${rawName}". Please enter the exact customer name.`);
        }

        if (matches.length === 1) {
          next.customer_id = matches[0].customer_id;
          next.customer_email = matches[0].email;
          // Fetch addresses right away for later selection
          await ensureAddressesLoaded(Number(next.customer_id));
          setFlowSync({ kind: "createAsset", step: "asset_type_pick", data: next });
          append({ role: "assistant", text: `Selected customer: ${matches[0].name}${matches[0].email ? ` — ${matches[0].email}` : ""}` });
          // Continue automatically if asset type already present, else ask.
          if (next.asset_type_name) return void handleCreateAssetFlow(next.asset_type_name);
          return append({ role: "assistant", text: 'Asset type? (You can say: "asset type is <name>" or ask "what asset types do we have?")' });
        }

        next.customer_candidates = matches;
        setFlowSync({ kind: "createAsset", step: "customer_pick", data: next });
        append({ role: "assistant", text: renderCustomerChoices(matches) });
        return;
      }

      // Step: pick the correct customer among duplicates
      if (step === "customer_pick") {
        const candidates = Array.isArray(next.customer_candidates) ? next.customer_candidates : [];
        const idx = parsePickIndex(answer);
        if (!Number.isFinite(idx) || idx < 1 || idx > Math.min(10, candidates.length)) {
          append({ role: "assistant", text: "Please reply with the number for the correct customer." });
          append({ role: "assistant", text: renderCustomerChoices(candidates) });
          return;
        }
        const chosen = candidates[idx - 1];
        next.customer_id = chosen.customer_id;
        next.customer_email = chosen.email;
        next.customer_candidates = null;
        await ensureAddressesLoaded(Number(next.customer_id));
        setFlowSync({ kind: "createAsset", step: "asset_type_pick", data: next });
        append({ role: "assistant", text: `Selected customer: ${chosen.name}${chosen.email ? ` — ${chosen.email}` : ""}` });
        if (next.asset_type_name) return void handleCreateAssetFlow(next.asset_type_name);
        append({ role: "assistant", text: 'Asset type? (You can say: "asset type is <name>" or ask "what asset types do we have?")' });
        return;
      }

      // Step: asset type pick (by name or list)
      if (step === "asset_type_pick") {
        const types = await ensureAssetTypesLoaded();

        if (looksLikeAskForList(answer)) {
          next.asset_type_candidates = types;
          setFlowSync({ kind: "createAsset", step: "asset_type_pick", data: next });
          append({ role: "assistant", text: renderAssetTypeChoices(types) });
          return;
        }

        const maybeIdx = parsePickIndex(answer);
        if (Number.isFinite(maybeIdx) && maybeIdx >= 1 && maybeIdx <= Math.min(15, types.length)) {
          const chosen = types[maybeIdx - 1];
          next.asset_type_id = chosen.asset_type_id;
          next.asset_type_name = chosen.asset_type_name;
          setFlowSync({ kind: "createAsset", step: "asset_name", data: next });
          if (next.asset_name) return void handleCreateAssetFlow(next.asset_name);
          return append({ role: "assistant", text: 'Brand name? (Example: "brand is Samsung")' });
        }

        const rawTypeName = extractAssetTypeName(answer) || answer.trim();
        if (!rawTypeName) return ask("asset_type_pick", 'Asset type? (Say a name or ask "what asset types do we have?")');
        next.asset_type_name = rawTypeName;

        const normalized = rawTypeName.toLowerCase();
        const match = types.find((t: any) => String(t?.asset_type_name || "").toLowerCase() === normalized);
        if (match) {
          next.asset_type_id = match.asset_type_id;
          setFlowSync({ kind: "createAsset", step: "asset_name", data: next });
          if (next.asset_name) return void handleCreateAssetFlow(next.asset_name);
          return append({ role: "assistant", text: 'Brand name? (Example: "brand is Samsung")' });
        }

        const partial = types.filter((t: any) =>
          String(t?.asset_type_name || "").toLowerCase().includes(normalized),
        );
        if (partial.length === 1) {
          next.asset_type_id = partial[0].asset_type_id;
          next.asset_type_name = partial[0].asset_type_name;
          setFlowSync({ kind: "createAsset", step: "asset_name", data: next });
          if (next.asset_name) return void handleCreateAssetFlow(next.asset_name);
          return append({ role: "assistant", text: 'Brand name? (Example: "brand is Samsung")' });
        }

        // Not found -> show list
        append({
          role: "assistant",
          text: `I couldn't match "${rawTypeName}". Here are the available asset types:`,
        });
        append({ role: "assistant", text: renderAssetTypeChoices(types) });
        return;
      }

      // Step: brand name (asset_name)
      if (step === "asset_name") {
        const brand = extractBrandName(answer) || answer.trim();
        if (!brand) return ask("asset_name", "Brand name?");
        next.asset_name = brand;
        setFlowSync({ kind: "createAsset", step: "asset_sku", data: next });
        if (next.asset_sku) return void handleCreateAssetFlow(next.asset_sku);
        return append({ role: "assistant", text: 'SKU? (Example: "sku is ABC123")' });
      }

      // Step: SKU
      if (step === "asset_sku") {
        const sku = extractSku(answer) || answer.trim();
        if (!sku) return ask("asset_sku", "SKU?");
        next.asset_sku = sku;
        setFlowSync({ kind: "createAsset", step: "asset_purchased_date", data: next });
        if (next.asset_purchased_date) return void handleCreateAssetFlow(next.asset_purchased_date);
        return append({ role: "assistant", text: 'Purchased date? (Example: "27th April 2026" or "2026-04-27")' });
      }

      // Step: Purchased date
      if (step === "asset_purchased_date") {
        const ymd = parseNaturalDateToYmd(answer);
        if (!ymd) return ask("asset_purchased_date", "Please enter a valid purchased date (example: 27th April 2026).");
        next.asset_purchased_date = ymd;

        const addresses = await ensureAddressesLoaded(Number(next.customer_id));
        if (!addresses.length) {
          return append({
            role: "assistant",
            text: "This customer has no addresses. Please add an address first, then try again.",
          });
        }
        if (addresses.length === 1) {
          next.address_id = addresses[0].address_id;
          setFlowSync({ kind: "createAsset", step: "confirm", data: next });
          const draft =
            `Please confirm:\n` +
            `Customer: ${next.customer_name}${next.customer_email ? ` — ${next.customer_email}` : ""}\n` +
            `Asset Type: ${next.asset_type_name}\n` +
            `Brand: ${next.asset_name}\n` +
            `SKU: ${next.asset_sku}\n` +
            `Purchased Date: ${next.asset_purchased_date}\n\n` +
            `Create this asset? (yes/no)`;
          append({ role: "assistant", text: draft });
          return;
        }
        setFlowSync({ kind: "createAsset", step: "address_pick", data: next });
        append({ role: "assistant", text: renderAddressChoices(addresses) });
        return;
      }

      // Step: Address pick
      if (step === "address_pick") {
        const addresses = Array.isArray(next.__addresses) ? next.__addresses : [];
        const idx = parsePickIndex(answer);
        if (!Number.isFinite(idx) || idx < 1 || idx > Math.min(10, addresses.length)) {
          append({ role: "assistant", text: "Please reply with the number for the correct address." });
          append({ role: "assistant", text: renderAddressChoices(addresses) });
          return;
        }
        const chosen = addresses[idx - 1];
        next.address_id = chosen.address_id;
        setFlowSync({ kind: "createAsset", step: "confirm", data: next });
        const draft =
          `Please confirm:\n` +
          `Customer: ${next.customer_name}${next.customer_email ? ` — ${next.customer_email}` : ""}\n` +
          `Asset Type: ${next.asset_type_name}\n` +
          `Brand: ${next.asset_name}\n` +
          `SKU: ${next.asset_sku}\n` +
          `Purchased Date: ${next.asset_purchased_date}\n\n` +
          `Create this asset? (yes/no)`;
        append({ role: "assistant", text: draft });
        return;
      }

      // Step: Confirm create asset
      if (step === "confirm") {
        const t = answer.toLowerCase();
        if (t.includes("no") || t.includes("cancel")) {
          append({ role: "assistant", text: "Cancelled. Let me know if you want to start over." });
          setFlowSync({ kind: "idle" });
          return;
        }
        if (!(t.includes("yes") || t.includes("confirm"))) {
          append({ role: "assistant", text: 'Please reply "yes" to confirm or "no" to cancel.' });
          return;
        }

        append({ role: "assistant", text: "Creating asset…" });
        await axiosInstance.post("/assets", {
          customer_id: next.customer_id,
          asset_type_id: next.asset_type_id,
          asset_name: next.asset_name,
          asset_sku: next.asset_sku,
          asset_purchased_date: next.asset_purchased_date,
          address_id: next.address_id,
        });
        append({ role: "assistant", text: "Asset created successfully." });
        setFlowSync({ kind: "idle" });
        return;
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const handleCreateTicketFlow = async (answer: string) => {
    const current = flowRef.current;
    if (current.kind !== "createTicket") return;
    let next = { ...current.data };
    const step = current.step;

    type CreateTicketStep = Extract<Flow, { kind: "createTicket" }>["step"];
    const ask = (nextStep: CreateTicketStep, prompt: string) => {
      setFlowSync({ kind: "createTicket", step: nextStep, data: next });
      append({ role: "assistant", text: prompt });
    };

    const searchCustomers = async (query: string) => {
      const q = String(query || "").trim();
      if (!q) return [];
      const resp = await axiosInstance.get("/customer/search", {
        params: { q, limit: 25 },
        timeout: 10000,
      });
      return Array.isArray(resp.data?.list) ? resp.data.list : [];
    };

    const fetchAssetsForCustomer = async (customerId: number) => {
      const resp = await axiosInstance.get(`/assets/customer/${customerId}`, { timeout: 15000 });
      return Array.isArray(resp.data?.list) ? resp.data.list : [];
    };

    const fetchCategoriesForAssetType = async (assetTypeId: number | string) => {
      const resp = await axiosInstance.get(`/category/asset-type/${assetTypeId}`, {
        params: { is_active: 1 },
        timeout: 15000,
      });
      return Array.isArray(resp.data?.list) ? resp.data.list : [];
    };

    const renderCustomerChoices = (candidates: any[]) => {
      const lines = candidates.slice(0, 10).map((c, i) => {
        const email = c?.email ? ` — ${c.email}` : "";
        const phone = c?.phone ? ` (${c.phone})` : "";
        return `${i + 1}. ${c.name}${email}${phone}`;
      });
      return `I found multiple customers with that name. Which one?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(
        10,
        candidates.length,
      )}).`;
    };

    const renderAssetChoices = (assets: any[]) => {
      const lines = assets.slice(0, 10).map((a, i) => {
        const sku = a?.asset_sku ? ` — SKU: ${a.asset_sku}` : "";
        const svc = a?.asset_service_id ? ` (#${a.asset_service_id})` : "";
        return `${i + 1}. ${a.asset_name || "Asset"}${svc}${sku}`;
      });
      return `Which asset is this ticket for?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(
        10,
        assets.length,
      )}).`;
    };

    const renderCategoryChoices = (cats: any[]) => {
      const lines = cats.slice(0, 15).map((c, i) => `${i + 1}. ${c.name}`);
      return `Which category?\n\n${lines.join("\n")}\n\nReply with a number (1-${Math.min(15, cats.length)}).`;
    };

    try {
      // Step: customer lookup
      if (step === "customer_lookup") {
        const extracted = extractCustomerNameForAsset(answer) || answer.trim();
        const rawName = normalizeCustomerSearchQuery(extracted);
        if (!rawName) return ask("customer_lookup", "Customer name?");
        next.customer_name = rawName;

        let customers: any[] = [];
        try {
          customers = await searchCustomers(rawName);
        } catch (err: any) {
          return ask(
            "customer_lookup",
            err?.code === "ECONNABORTED"
              ? "Customer search timed out. Please try again."
              : "Customer search failed. Please try again.",
          );
        }

        if (customers.length === 0) {
          next.customer_name = "";
          return ask("customer_lookup", `No customer found for "${rawName}". Please enter the exact customer name.`);
        }

        // Prefer exact match if present
        const exact = customers.filter((c: any) => String(c?.name || "").toLowerCase() === rawName.toLowerCase());
        const matches = exact.length ? exact : customers;

        if (matches.length === 1) {
          const chosen = matches[0];
          next.customer_id = chosen.customer_id;
          next.customer_email = chosen.email;
          setFlowSync({ kind: "createTicket", step: "asset_pick", data: next });
          append({ role: "assistant", text: `Selected customer: ${chosen.name}${chosen.email ? ` — ${chosen.email}` : ""}` });
          // Continue: try asset query if present, else ask.
          if (next.asset_query_sku || next.asset_query_name) return void handleCreateTicketFlow(answer);
          return append({ role: "assistant", text: 'Asset? (You can say: "asset is <name>" or "sku is <value>")' });
        }

        next.customer_candidates = matches;
        setFlowSync({ kind: "createTicket", step: "customer_pick", data: next });
        append({ role: "assistant", text: renderCustomerChoices(matches) });
        return;
      }

      // Step: customer pick
      if (step === "customer_pick") {
        const candidates = Array.isArray(next.customer_candidates) ? next.customer_candidates : [];
        const idx = parsePickIndex(answer);
        if (!Number.isFinite(idx) || idx < 1 || idx > Math.min(10, candidates.length)) {
          append({ role: "assistant", text: "Please reply with the number for the correct customer." });
          append({ role: "assistant", text: renderCustomerChoices(candidates) });
          return;
        }
        const chosen = candidates[idx - 1];
        next.customer_id = chosen.customer_id;
        next.customer_email = chosen.email;
        next.customer_candidates = null;
        setFlowSync({ kind: "createTicket", step: "asset_pick", data: next });
        append({ role: "assistant", text: `Selected customer: ${chosen.name}${chosen.email ? ` — ${chosen.email}` : ""}` });
        if (next.asset_query_sku || next.asset_query_name) return void handleCreateTicketFlow(answer);
        append({ role: "assistant", text: 'Asset? (You can say: "asset is <name>" or "sku is <value>")' });
        return;
      }

      // Step: asset pick
      if (step === "asset_pick") {
        if (!next.customer_id) return ask("customer_lookup", "Customer name?");

        // Allow selecting by number if a list was shown
        const idx = parsePickIndex(answer);
        const assetsAll = await fetchAssetsForCustomer(Number(next.customer_id));
        if (!assetsAll.length) {
          return append({ role: "assistant", text: "This customer has no assets. Please create an asset first." });
        }

        if (Number.isFinite(idx) && next.asset_candidates && Array.isArray(next.asset_candidates)) {
          const candidates = next.asset_candidates as any[];
          if (idx < 1 || idx > Math.min(10, candidates.length)) {
            append({ role: "assistant", text: "Please reply with the number for the correct asset." });
            append({ role: "assistant", text: renderAssetChoices(candidates) });
            return;
          }
          const chosen = candidates[idx - 1];
          next.asset_id = chosen.asset_id;
          next.asset_type_id = chosen.asset_type_id;
          next.address_id = chosen.address_id;
          next.asset_candidates = null;
          setFlowSync({ kind: "createTicket", step: "category_pick", data: next });
          append({ role: "assistant", text: `Selected asset: ${chosen.asset_name || "Asset"}${chosen.asset_sku ? ` — SKU: ${chosen.asset_sku}` : ""}` });
          // Continue to category if provided
          if (next.category_name) return void handleCreateTicketFlow(next.category_name);
          return append({ role: "assistant", text: 'Category? (You can ask "what categories do we have?" )' });
        }

        // Extract asset query from the current answer (or from seeded data)
        const extracted = extractAssetQueryForTicket(answer);
        const qSku = extracted.sku || next.asset_query_sku || "";
        const qName = (extracted.assetName || next.asset_query_name || "").trim();
        next.asset_query_sku = qSku;
        next.asset_query_name = qName;

        let matches = assetsAll;
        if (qSku) {
          const nSku = qSku.toLowerCase();
          matches = assetsAll.filter((a: any) => String(a?.asset_sku || "").toLowerCase() === nSku);
        } else if (qName) {
          const nName = qName.toLowerCase();
          matches = assetsAll.filter((a: any) => String(a?.asset_name || "").toLowerCase().includes(nName));
        }

        if (matches.length === 1) {
          const chosen = matches[0];
          next.asset_id = chosen.asset_id;
          next.asset_type_id = chosen.asset_type_id;
          next.address_id = chosen.address_id;
          setFlowSync({ kind: "createTicket", step: "category_pick", data: next });
          append({ role: "assistant", text: `Selected asset: ${chosen.asset_name || "Asset"}${chosen.asset_sku ? ` — SKU: ${chosen.asset_sku}` : ""}` });
          if (next.category_name) return void handleCreateTicketFlow(next.category_name);
          return append({ role: "assistant", text: 'Category? (You can ask "what categories do we have?" )' });
        }

        if (matches.length > 1) {
          next.asset_candidates = matches;
          setFlowSync({ kind: "createTicket", step: "asset_pick", data: next });
          append({ role: "assistant", text: renderAssetChoices(matches) });
          return;
        }

        // No matches for query -> show all assets to pick
        next.asset_candidates = assetsAll;
        setFlowSync({ kind: "createTicket", step: "asset_pick", data: next });
        append({ role: "assistant", text: `I couldn't match that asset. Here are this customer's assets:` });
        append({ role: "assistant", text: renderAssetChoices(assetsAll) });
        return;
      }

      // Step: category pick
      if (step === "category_pick") {
        if (!next.asset_type_id) return ask("asset_pick", "Asset?");
        const cats = await fetchCategoriesForAssetType(next.asset_type_id);
        if (!cats.length) {
          return ask("category_pick", "No categories found for this asset type. Please add categories first.");
        }

        if (looksLikeAskForCategories(answer)) {
          next.category_candidates = cats;
          setFlowSync({ kind: "createTicket", step: "category_pick", data: next });
          append({ role: "assistant", text: renderCategoryChoices(cats) });
          return;
        }

        const idx = parsePickIndex(answer);
        if (Number.isFinite(idx) && idx >= 1 && idx <= Math.min(15, cats.length)) {
          const chosen = cats[idx - 1];
          next.category_id = chosen.category_id;
          next.category_name = chosen.name;
          setFlowSync({ kind: "createTicket", step: "ticket_title", data: next });
          if (next.title) return void handleCreateTicketFlow(next.title);
          return append({ role: "assistant", text: 'Ticket title? (Example: "AC not cooling")' });
        }

        const rawCat = extractCategoryName(answer) || answer.trim();
        if (!rawCat) return ask("category_pick", 'Category? (Say a name or ask "what categories do we have?")');
        next.category_name = rawCat;

        const normalized = rawCat.toLowerCase();
        const exact = cats.find((c: any) => String(c?.name || "").toLowerCase() === normalized);
        if (exact) {
          next.category_id = exact.category_id;
          setFlowSync({ kind: "createTicket", step: "ticket_title", data: next });
          if (next.title) return void handleCreateTicketFlow(next.title);
          return append({ role: "assistant", text: 'Ticket title? (Example: "AC not cooling")' });
        }
        const partial = cats.filter((c: any) => String(c?.name || "").toLowerCase().includes(normalized));
        if (partial.length === 1) {
          next.category_id = partial[0].category_id;
          next.category_name = partial[0].name;
          setFlowSync({ kind: "createTicket", step: "ticket_title", data: next });
          if (next.title) return void handleCreateTicketFlow(next.title);
          return append({ role: "assistant", text: 'Ticket title? (Example: "AC not cooling")' });
        }

        append({ role: "assistant", text: `I couldn't match "${rawCat}". Here are the available categories:` });
        append({ role: "assistant", text: renderCategoryChoices(cats) });
        return;
      }

      // Step: title
      if (step === "ticket_title") {
        const t = extractTicketTitle(answer) || answer.trim();
        if (!t) return ask("ticket_title", "Ticket title?");
        next.title = t;
        setFlowSync({ kind: "createTicket", step: "ticket_description", data: next });
        if (next.description) return void handleCreateTicketFlow(next.description);
        return append({ role: "assistant", text: "Ticket description?" });
      }

      // Step: description
      if (step === "ticket_description") {
        const d = extractTicketDescription(answer) || answer.trim();
        if (!d) return ask("ticket_description", "Ticket description?");
        next.description = d;
        setFlowSync({ kind: "createTicket", step: "confirm", data: next });
        const draft =
          `Please confirm:\n` +
          `Customer: ${next.customer_name}${next.customer_email ? ` — ${next.customer_email}` : ""}\n` +
          `Asset ID: ${next.asset_id}\n` +
          `Category: ${next.category_name}\n` +
          `Title: ${next.title}\n` +
          `Description: ${next.description}\n\n` +
          `Create this ticket? (yes/no)`;
        append({ role: "assistant", text: draft });
        return;
      }

      // Step: confirm
      if (step === "confirm") {
        const t = answer.toLowerCase();
        if (t.includes("no") || t.includes("cancel")) {
          append({ role: "assistant", text: "Cancelled. Let me know if you want to start over." });
          setFlowSync({ kind: "idle" });
          return;
        }
        if (!(t.includes("yes") || t.includes("confirm"))) {
          append({ role: "assistant", text: 'Please reply "yes" to confirm or "no" to cancel.' });
          return;
        }
        if (!next.asset_id || !next.asset_type_id || !next.category_id || !next.address_id) {
          append({ role: "assistant", text: "Missing required details (asset/category/address). Please start again." });
          setFlowSync({ kind: "idle" });
          return;
        }
        append({ role: "assistant", text: "Creating ticket…" });
        await axiosInstance.post("/tickets", {
          ticketData: {
            title: next.title,
            description: next.description,
            category_id: next.category_id,
            asset_id: next.asset_id,
            asset_type_id: next.asset_type_id,
            address_id: next.address_id,
            customer_id: next.customer_id,
            status_id: 1,
          },
          mediaData: [],
        });
        append({ role: "assistant", text: "Ticket created successfully." });
        setFlowSync({ kind: "idle" });
        return;
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      append({ role: "assistant", text: `Failed: ${String(msg)}` });
      setFlowSync({ kind: "idle" });
    }
  };

  const toggleMic = () => {
    if (!speechSupported) return;
    if (listening) {
      micWantedRef.current = false;
      try {
        recognitionRef.current?.stop?.();
      } catch (_) {}
      setListening(false);
      return;
    }
    const SR = getSpeechRecognition();
    if (!SR) return;
    const rec = new SR();
    recognitionRef.current = rec;
    rec.lang = "en-IN";
    rec.continuous = true;
    rec.interimResults = true;
    micWantedRef.current = true;
    rec.onstart = () => {
      setListening(true);
      append({ role: "assistant", text: 'Listening… say "Hey Servix".' });
    };
    rec.onresult = (event: any) => {
      const last = event.results?.[event.results.length - 1];
      const transcript = last?.[0]?.transcript?.toString?.()?.trim?.() ?? "";
      if (!transcript) return;
      lastSpeechTextRef.current = transcript;
      const t = normalizeSpokenText(transcript);
      // Wake word behavior: only start responding after wake phrase.
      const hit = WAKE_PHRASES.find((p) => t.includes(p));
      if (hit) {
        const idx = t.indexOf(hit);
        // Use normalized string slicing for tail; transcript may have punctuation,
        // so fall back to removing wake phrase text from normalized tail.
        const tail = t.slice(idx + hit.length).trim();
        if (tail) {
          // Treat "Hey Tix <command>" as a message.
          handleSend(tail);
        } else {
          append({ role: "assistant", text: "Hi! How can I help you today?" });
        }
        return;
      }
      // If already listening, treat any phrase as input.
      setInput(transcript);

      // Auto-send 2s after the user stops speaking (no new results).
      // We intentionally debounce on *any* result (interim/final) because the
      // browser can emit early "final" segments mid-sentence (e.g. "create
      // customer" becomes final, then the user continues "and name is ...").
      if (autoSendTimerRef.current) {
        window.clearTimeout(autoSendTimerRef.current);
        autoSendTimerRef.current = null;
      }
      autoSendTimerRef.current = window.setTimeout(() => {
        autoSendTimerRef.current = null;
        const textToSend = (lastSpeechTextRef.current || "").trim();
        if (!textToSend) return;
        if (!canAutoSendVoiceForCurrentStep(textToSend)) return;
        handleSend(textToSend);
        setInput("");
      }, 2000);
    };
    rec.onerror = (e: any) => {
      setListening(false);
      micWantedRef.current = false;
      append({
        role: "assistant",
        text:
          "Voice input failed. If you're on HTTP or blocked mic permission, speech may not work. Please allow microphone access and retry.",
      });
    };
    rec.onend = () => {
      // Keep mic open until user explicitly turns it off.
      if (micWantedRef.current) {
        try {
          rec.start();
          return;
        } catch (_) {}
      }
      setListening(false);
    };
    try {
      rec.start();
    } catch (_) {
      micWantedRef.current = false;
      setListening(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
    <Box p={3}>
      <Card sx={{ p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <SmartToyIcon />
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              Hey Tix
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI assistant for tickets and customer operations
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={toggleMic} disabled={!speechSupported} aria-label="toggle mic">
              {listening ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            height: 420,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 1,
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.03)",
          }}
        >
          {messages.map((m, idx) => (
            <Box
              key={idx}
              sx={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "80%",
                p: 1.5,
                borderRadius: 2,
                bgcolor: m.role === "user" ? "primary.main" : "white",
                color: m.role === "user" ? "primary.contrastText" : "text.primary",
                whiteSpace: "pre-wrap",
              }}
            >
              <Typography variant="body2">{m.text}</Typography>
            </Box>
          ))}
          <div ref={scrollEndRef} />
        </Box>

        <Box sx={{ mt: 2, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "stretch",
              width: "100%",
            }}
          >
            <TextField className="ai-voice"
              fullWidth
              multiline
              minRows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder='Type… (or say "Hey Servix")'
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              sx={{ flex: 1, width: "100% !important" }}
            />
            <Button
              variant="contained"
              onClick={handleSendClick}
              sx={{ minWidth: 110, flexShrink: 0, alignSelf: "stretch" }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
    </DashboardLayout>
  );
}

